import { AfterViewInit, Component, Input, OnInit, } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MaterialModule } from 'src/app/material.module';
import { MatTableModule } from '@angular/material/table';
import { MatCardModule } from '@angular/material/card';
import { IndianCurrencyPipe } from 'src/app/pipe/indian-currency.pipe';
import { TranslateModule } from '@ngx-translate/core';
import { DiscountType } from 'src/app/common/enums/discount-type';
import { BillType } from 'src/app/common/enums/bill-type';
import { environment } from 'src/environments/environment';
import { FallbackImageDirective } from 'src/app/common/directive/fallback-image.directive';
import { HttpClient } from '@angular/common/http';
import { HospitalStateService } from 'src/app/services/hospitalstate.service';
import { PrintSettingService } from '../../settings/hospital-setting/print-setting/print-setting.service';
import { firstValueFrom } from 'rxjs';

@Component({
    selector: 'app-bill-print',
    standalone: true,
    imports: [
        CommonModule,
        MaterialModule,
        MatTableModule,
        MatCardModule,
        IndianCurrencyPipe,
        TranslateModule,
        FallbackImageDirective
    ],
    providers: [
        IndianCurrencyPipe,
        DatePipe
    ],
    templateUrl: './bill-print.component.html'
})
export class BillPrintComponent implements OnInit, AfterViewInit {
    @Input() billData: any;
    @Input() doctorInfo: any;
    @Input() hospitalInfo: any;
    @Input() successResult: any;
    @Input() billType?: BillType;
    DiscountType = DiscountType;
    BillType = BillType;
    charges: any[] = [];
    today: Date = new Date();
    staticimagepath = environment.imagePath;

    constructor(
        private http: HttpClient,
        private hospitalState: HospitalStateService,
        private printSettingService: PrintSettingService,
        private datePipe: DatePipe,
        private indianCurrencyPipe: IndianCurrencyPipe
    ) { }

    ngOnInit(): void {
        this.charges = this.billData?.billDetails ?? [];
    }

    ngAfterViewInit(): void { }

    get isIPDBill(): boolean {
        return this.billType === BillType.IPDBill || !!this.billData?.ipdNumber;
    }

    get discountSymbol(): string {
        const discountType = DiscountType[this.billData?.discountType as keyof typeof DiscountType];
        if (discountType === DiscountType.FixedAmount) {
            return '₹';
        }
        return '%';
    }

    async createPrintContent(): Promise<string> {
        try {
            let template = await firstValueFrom(
                this.http.get('assets/print-template.html', { responseType: 'text' })
            ) || '';

            const hospitalSafe = this.hospitalState.getHospital();
            const settings = await this.printSettingService.getPrintSettingPromise(hospitalSafe.id);

            // Format charges table
            let tableRows = '';
            (this.billData?.billDetails || []).forEach((element: any, i: number) => {
                tableRows += `
                    <tr>
                        <td style="border: 1px solid #ccc; padding: 8px;">${i + 1}</td>
                        <td class="text-left" style="border: 1px solid #ccc; padding: 8px;">${element.chargeName}</td>
                        <td style="border: 1px solid #ccc; padding: 8px;">${element.quantity}</td>
                        <td style="border: 1px solid #ccc; padding: 8px; text-align: right;">${this.indianCurrencyPipe.transform(element.rate)}</td>
                        <td style="border: 1px solid #ccc; padding: 8px; text-align: right;">${this.indianCurrencyPipe.transform(element.total || element.totalCharge)}</td>
                    </tr>
                `;
            });

            const amountInWords = this.successResult?.amountInWords || this.billData?.amountInWords || '';
            const billNo = this.successResult?.billNo || this.billData?.billNo || '';
            const billDate = this.datePipe.transform(this.successResult?.billDate || this.billData?.billDate, 'dd-MMM-yyyy') || '';
            const opdNo = this.billData?.opdNumber || '';
            const opdDate = this.datePipe.transform(this.billData?.opdDate, 'dd-MMM-yyyy') || '';
            const ipdNo = this.billData?.ipdNumber || '';
            const ipdDate = this.datePipe.transform(this.billData?.admissionDate, 'dd-MMM-yyyy') || '';

            const billDetailsCenter = `
                <div style="text-align: center; margin-bottom: 10px;">
                    <h2 style="margin: 0 0 10px 0; text-transform: uppercase; text-decoration: underline; font-size: 18px;">Bill Cum Receipt</h2>
                </div>
                <div style="text-align: center; margin-bottom: 10px; font-size: 14px; border-bottom: 1px solid #eee; padding-bottom: 10px;">
                    <div style="margin-bottom: 5px;"><strong>Bill No:</strong> ${billNo} &nbsp;&nbsp;&nbsp;&nbsp; <strong>Bill Date:</strong> ${billDate}</div>
                    ${this.isIPDBill ?
                    `<div><strong>IPD No:</strong> ${ipdNo} &nbsp;&nbsp;&nbsp;&nbsp; <strong>IPD Date:</strong> ${ipdDate}</div>` :
                    `<div><strong>OPD No:</strong> ${opdNo} &nbsp;&nbsp;&nbsp;&nbsp; <strong>OPD Date:</strong> ${opdDate}</div>`
                }
                </div>
            `;

            const sectionContent = `
                ${billDetailsCenter}
                <div style="font-size: 14px;">
                    <p style="margin: 5px 0;"><strong>Received with thanks from:</strong> ${this.billData?.patientName}</p>
                    <p style="margin: 0 0 15px 0;"><strong>The sum of money:</strong> ₹${this.billData?.grandTotal} (${amountInWords})</p>
                </div>
                <div class="print-table-wrapper">
                    <table class="table table-bordered" style="width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 14px;">
                        <thead>
                            <tr style="background-color: #f2f2f2;">
                                <th style="border: 1px solid #ccc; padding: 5px; text-align: left;">No.</th>
                                <th style="border: 1px solid #ccc; padding: 5px; text-align: left;">Charge</th>
                                <th style="border: 1px solid #ccc; padding: 5px; text-align: left;">Qty</th>
                                <th style="border: 1px solid #ccc; padding: 5px; text-align: right;">Rate</th>
                                <th style="border: 1px solid #ccc; padding: 5px; text-align: right;">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${tableRows}
                        </tbody>
                    </table>
                </div>
                <div class="summary-totals" style="text-align: right; margin-top: 20px; font-size: 14px;">
                    <p style="margin: 5px 0;"><strong>Sub Total:</strong> ${this.indianCurrencyPipe.transform(this.billData?.subTotal)}</p>
                    <p style="margin: 5px 0;"><strong>Discount (${this.discountSymbol}):</strong> ${this.billData?.discount}</p>
                    <div style="margin-top: 10px; font-size: 18px;">
                        <strong>Total:</strong> ${this.indianCurrencyPipe.transform(this.billData?.grandTotal)}
                    </div>
                </div>
            `;

            const fullImagePath = hospitalSafe.logo ? `${this.staticimagepath}${hospitalSafe.logo}` : null;
            const defaultLogo = `${window.location.origin}/assets/images/svgs/hospital.png`;

            const replacements: Record<string, string> = {
                reportTitle: 'Bill Cum Receipt',
                hospitalName: hospitalSafe.name || '',
                hospitalAddress: hospitalSafe.address || '',
                phone: hospitalSafe.phone || '',
                reportSections: sectionContent,
                doctorName: this.doctorInfo?.name || '',
                qualification: this.doctorInfo?.degree || '',
                email: hospitalSafe.email || '',
                logo: `<img src="${fullImagePath || defaultLogo}" class="logo" onerror="this.onerror=null;this.src='${defaultLogo}'"/>`,
                patientName: this.billData?.patientName || '',
                patientAge: '',
                opdNumber: opdNo,
                opdDate: opdDate
            };

            let filledTemplate = template;
            for (const key in replacements) {
                const value = replacements[key] ?? '';
                const regex = new RegExp(`{{\\s*${key}\\s*}}`, 'g');
                filledTemplate = filledTemplate.replace(regex, value);
            }

            // Apply settings
            if (settings) {
                const logoPos = settings.logoPosition === 'center' ? 'column' : (settings.logoPosition === 'left' ? 'row' : 'row-reverse');
                const headerClass = settings.logoPosition === 'center' ? 'center' : '';
                const headerAlignment = settings.addressAlignment || 'left';

                const styles = `
                    :root {
                        --logo-size: ${settings.logoSize || '60px'};
                        --logo-position: ${logoPos};
                        --header-alignment: ${headerAlignment};
                        --hospital-name-size: ${settings.hospitalNameSize || 20}px;
                        --address-font-size: ${settings.addressFontSize || 13}px;
                        --footer-display: ${settings.showFooter ? 'block' : 'none'};
                        --doctor-signature-top-display: ${settings.showFooter && settings.doctorNamePosition === 'top' ? 'block' : 'none'};
                        --doctor-signature-bottom-display: ${settings.showFooter && settings.doctorNamePosition === 'footer' ? 'block' : 'none'};
                    }
                    .table-bordered th, .table-bordered td { border: 1px solid #ccc !important; }
                `;
                filledTemplate = filledTemplate.replace('</head>', `<style>${styles}</style></head>`).replace('{{headerClass}}', headerClass);
            } else {
                filledTemplate = filledTemplate.replace('{{headerClass}}', '');
            }

            return filledTemplate;
        } catch (error) {
            console.error('Error creating bill print content:', error);
            throw error;
        }
    }
}